// src/helpers/device.ts
import FingerprintJS, {
  type UnknownComponents,
} from '@fingerprintjs/fingerprintjs';

const DEVICE_ID_KEY = 'peliswan_device_id';
const DB_NAME = 'peliswan_device';
const STORE_NAME = 'identity';

const STABLE_COMPONENTS = [
  'platform',
  'architecture',
  'osCpu',
  'hardwareConcurrency',
  'deviceMemory',
  'vendor',
  'math',
  'webGlBasics',
  'touchSupport',
] as const;

export interface DeviceInfo {
  deviceId: string; // assigned once, never derived from hardware
  configHash: string; // hash of the stable components only
  os: string;
  osVersion: string;
  browser: string;
  browserVersion: string;
  cores: number;
  memory: number;
  timezone: string;
}

function openDeviceDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1);
    request.onupgradeneeded = () => request.result.createObjectStore(STORE_NAME);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function readIndexedDb(): Promise<string> {
  try {
    const db = await openDeviceDb();
    try {
      return await new Promise<string>((resolve, reject) => {
        const request = db
          .transaction(STORE_NAME, 'readonly')
          .objectStore(STORE_NAME)
          .get(DEVICE_ID_KEY);
        request.onsuccess = () => resolve(request.result || '');
        request.onerror = () => reject(request.error);
      });
    } finally {
      db.close();
    }
  } catch {
    return '';
  }
}

async function writeIndexedDb(deviceId: string): Promise<void> {
  try {
    const db = await openDeviceDb();
    try {
      await new Promise<void>((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, 'readwrite');
        tx.objectStore(STORE_NAME).put(deviceId, DEVICE_ID_KEY);
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
      });
    } finally {
      db.close();
    }
  } catch {}
}

function readLocalStorage(): string {
  try {
    return localStorage.getItem(DEVICE_ID_KEY) || '';
  } catch {
    return '';
  }
}

function writeLocalStorage(deviceId: string): void {
  try {
    localStorage.setItem(DEVICE_ID_KEY, deviceId);
  } catch {}
}

function createDeviceId(): string {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();

  const bytes = crypto.getRandomValues(new Uint8Array(16));
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

// Generated once and mirrored across both stores, so clearing either one restores from the other.
export async function getDeviceId(): Promise<string> {
  const [fromLocalStorage, fromIndexedDb] = await Promise.all([
    Promise.resolve(readLocalStorage()),
    readIndexedDb(),
  ]);

  const deviceId = fromLocalStorage || fromIndexedDb || createDeviceId();

  if (fromLocalStorage !== deviceId) writeLocalStorage(deviceId);
  if (fromIndexedDb !== deviceId) await writeIndexedDb(deviceId);

  return deviceId;
}

async function getConfigHash(): Promise<string> {
  try {
    const agent = await FingerprintJS.load();
    const { components } = await agent.get();

    const stable = STABLE_COMPONENTS.reduce<UnknownComponents>((picked, key) => {
      if (key in components) picked[key] = (components as any)[key];
      return picked;
    }, {});

    return FingerprintJS.hashComponents(stable);
  } catch {
    return '';
  }
}

function getWindowsVersion(platformVersion: string): string {
  return parseInt(platformVersion.split('.')[0]) >= 13 ? '11' : '10';
}

function isRealBrand(brand: string): boolean {
  return !brand.includes('Not') && !brand.includes('Brand') && brand !== 'Chromium';
}

function getBrowserFromUserAgent(): { browser: string; browserVersion: string } {
  const ua = navigator.userAgent;
  const patterns: [string, RegExp][] = [
    ['Microsoft Edge', /Edg\/([\d.]+)/],
    ['Opera', /OPR\/([\d.]+)/],
    ['Firefox', /Firefox\/([\d.]+)/],
    ['Chrome', /Chrome\/([\d.]+)/],
    ['Safari', /Version\/([\d.]+).*Safari/],
  ];

  for (const [browser, pattern] of patterns) {
    const match = ua.match(pattern);
    if (match) return { browser, browserVersion: match[1] };
  }

  return { browser: 'Unknown', browserVersion: 'Unknown' };
}

function getOsFromUserAgent(): { os: string; osVersion: string } {
  const ua = navigator.userAgent;
  const patterns: [string, RegExp][] = [
    ['Windows', /Windows NT ([\d.]+)/],
    ['Android', /Android ([\d.]+)/],
    ['iOS', /iPhone OS ([\d_]+)/],
    ['macOS', /Mac OS X ([\d_]+)/],
  ];

  for (const [os, pattern] of patterns) {
    const match = ua.match(pattern);
    if (!match) continue;

    const version = match[1].replace(/_/g, '.');
    return { os, osVersion: version === '10.0' ? '10' : version };
  }

  return { os: navigator.platform || 'Unknown', osVersion: 'Unknown' };
}

export async function getDeviceInfo(): Promise<DeviceInfo> {
  const nav = navigator as any;

  let hints: any = {};
  try {
    if (nav.userAgentData) {
      hints = await nav.userAgentData.getHighEntropyValues([
        'platform',
        'platformVersion',
        'fullVersionList',
      ]);
    }
  } catch {}

  const uaOs = getOsFromUserAgent();
  const uaBrowser = getBrowserFromUserAgent();

  const os = hints.platform || uaOs.os;
  const osVersion =
    hints.platform === 'Windows' && hints.platformVersion
      ? getWindowsVersion(hints.platformVersion)
      : hints.platformVersion || uaOs.osVersion;

  const brand = hints.fullVersionList?.find((entry: any) =>
    isRealBrand(entry.brand),
  );

  const [deviceId, configHash] = await Promise.all([
    getDeviceId(),
    getConfigHash(),
  ]);

  return {
    deviceId,
    configHash,
    os,
    osVersion: osVersion || 'Unknown',
    browser: brand?.brand || uaBrowser.browser,
    browserVersion: brand?.version || uaBrowser.browserVersion,
    cores: navigator.hardwareConcurrency || 0,
    memory: nav.deviceMemory ?? 0,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  };
}
